import { CSSProperties, JSX } from 'react';
import styled, { css } from 'styled-components';
import { colors } from '@ardoq/design-tokens';
import { aiGradientBackground, aiGradientFlow } from '@ardoq/style-helpers';
import { IconSize } from './types';

const STAR_PATH =
  'M10.5508 12.3428V16.1992H8.58789V12.3428H10.5508ZM8.58789 12.3428H6.625V10.4141H8.58789V12.3428ZM12.5127 12.3428H10.5508V10.4141H12.5127V12.3428ZM8.58789 8.48535H6.625V10.4141H2.7002V8.48535H6.625V6.55664H8.58789V8.48535ZM16.2002 10.4141H12.5127V8.48535H16.2002V10.4141ZM12.5127 8.48535H10.5508V6.55664H12.5127V8.48535ZM10.5508 6.55762H8.58789V2.7002H10.5508V6.55762Z';

const encodedMaskSvg = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18"><path fill="white" d="${STAR_PATH}"/></svg>`
);

const maskUrl = `url("data:image/svg+xml,${encodedMaskSvg}")`;

const maskStyles = css`
  mask-image: ${maskUrl};
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  -webkit-mask-image: ${maskUrl};
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
`;

export const AI_ICON_ANIMATED_CSS_VARIABLE = '--ai-icon-animated';

const StyledAiIconGradientAnimated = styled.span`
  position: relative;
  display: inline-block;
  width: ${IconSize.MEDIUM}px;
  height: ${IconSize.MEDIUM}px;
  color: ${colors.orange500};
  background-color: currentColor;
  ${maskStyles};
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${aiGradientBackground};
    background-size: 300% 300%;
    opacity: var(${AI_ICON_ANIMATED_CSS_VARIABLE}, 0);
    animation-name: ${aiGradientFlow};
    animation-duration: 2s;
    animation-timing-function: ease-in-out;
    /* Allows us to optimize animation when hidden. 9999 is effectively infinite */
    animation-iteration-count: calc(
      var(${AI_ICON_ANIMATED_CSS_VARIABLE}, 0) * 9999
    );
    transition: opacity 0.2s ease-out;
    ${maskStyles};
    pointer-events: none;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      transition: none;
    }
  }
`;

export type AiIconGradientAnimatedProps = {
  isActive?: true;
};

const AiIconGradientAnimated = ({
  isActive,
}: AiIconGradientAnimatedProps = {}): JSX.Element => {
  const fillStyle = isActive
    ? ({ [AI_ICON_ANIMATED_CSS_VARIABLE]: '1' } as CSSProperties)
    : undefined;
  return <StyledAiIconGradientAnimated aria-hidden="true" style={fillStyle} />;
};

const AI_ICON_GRADIENT_ANIMATED_SELECTOR =
  StyledAiIconGradientAnimated.toString();

export { AiIconGradientAnimated, AI_ICON_GRADIENT_ANIMATED_SELECTOR };
